import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { BillingPlanDialog } from "@/components/billing-plan/billing-plan-dialog";
import { WAGE_PASS_THROUGH_OFF_SUMMARY_LABEL } from "@/components/billing-plan/wage-pass-through-copy";
import { buttonVariants } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  USAGE_BILLING_COLUMN_COUNT,
  USAGE_BILLING_COLUMNS,
} from "./usage-billing-columns";
import {
  formatUsageBillingCurrency,
  type UsageBillingSummaryItem,
} from "./usage-billing-format";
import { INVOICES_SECTION_ID } from "./usage-billing-invoices-anchor";
import { UsageBillingUnitEvents } from "./usage-billing-unit-events";
import { UsageBillingCategoryUsage } from "./usage-billing-category-usage";
import { UsageBillingCycles } from "./usage-billing-cycles";

type UsageBillingUnitRowProps = {
  unit: UsageBillingSummaryItem;
  monthKey: string;
  canManageInvoices: boolean;
  isExpanded: boolean;
  onToggle: () => void;
};

export function UsageBillingUnitRow({
  unit,
  monthKey,
  canManageInvoices,
  isExpanded,
  onToggle,
}: UsageBillingUnitRowProps) {
  const categoriesByCode = new Map(
    unit.summary.categories.map((category) => [
      category.categoryCode,
      category,
    ]),
  );

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-gray-50"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <TableCell>
          <div className="flex items-center gap-2 pl-4">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
            )}
            <span className="font-medium text-gray-900">{unit.unitName}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 pl-10">
            {canManageInvoices && (
              <BillingPlanDialog
                organizationBusinessUnitId={unit.organizationBusinessUnitId}
              />
            )}
            <Link
              to="/admin/usage-billing/$organizationBusinessUnitId"
              params={{
                organizationBusinessUnitId: unit.organizationBusinessUnitId,
              }}
              search={{ month: monthKey }}
              hash={INVOICES_SECTION_ID}
              className={buttonVariants({ variant: "outline", size: "sm" })}
              onClick={(event) => event.stopPropagation()}
            >
              Invoices
            </Link>
          </div>
        </TableCell>
        {USAGE_BILLING_COLUMNS.map((column) => (
          <TableCell
            key={column.categoryCode}
            className="text-right tabular-nums"
          >
            <UsageBillingCategoryUsage
              category={categoriesByCode.get(column.categoryCode)}
            />
          </TableCell>
        ))}
        <TableCell className="text-right tabular-nums">
          {unit.summary.wagePassThroughAmount === null ? (
            <span className="text-xs text-muted-foreground">
              {WAGE_PASS_THROUGH_OFF_SUMMARY_LABEL}
            </span>
          ) : (
            formatUsageBillingCurrency(unit.summary.wagePassThroughAmount)
          )}
        </TableCell>
        <TableCell className="text-right tabular-nums font-medium">
          {formatUsageBillingCurrency(unit.summary.totalAmount)}
          {unit.summary.wagePassThroughAmount !== null && (
            <span className="block text-xs font-normal text-muted-foreground">
              {formatUsageBillingCurrency(unit.summary.saileChargesAmount)}{" "}
              Saile
            </span>
          )}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell
            colSpan={USAGE_BILLING_COLUMN_COUNT}
            className="bg-gray-50 p-0"
          >
            <UsageBillingCycles summary={unit.summary} monthKey={monthKey} />
            <UsageBillingUnitEvents
              organizationBusinessUnitId={unit.organizationBusinessUnitId}
              monthKey={monthKey}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
